class PWAService {
    constructor() {
        this.deferredPrompt = null;
        this.init();
    }

    init() {
        if (typeof window !== 'undefined') {
            window.addEventListener(
                'beforeinstallprompt',
                this.handleBeforeInstallPrompt.bind(this),
            );
            window.addEventListener('appinstalled', this.handleAppInstalled.bind(this));
        }
    }

    isPWA() {
        if (typeof window === 'undefined') return false;

        if (window.navigator.standalone === true) {
            return true;
        }

        if (window.matchMedia('(display-mode: standalone)').matches) {
            return true;
        }

        if (localStorage.getItem('pwa-installed') === 'true') {
            return true;
        }

        return false;
    }

    isMobile() {
        if (typeof window === 'undefined') return false;
        return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    }

    isIOS() {
        if (typeof window === 'undefined') return false;
        return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    }

    isAndroid() {
        if (typeof window === 'undefined') return false;
        return /Android/i.test(navigator.userAgent);
    }

    shouldShowPrompt() {
        if (this.isPWA()) {
            return false;
        }

        if (!this.isMobile()) {
            return false;
        }

        const hasSeenPrompt = localStorage.getItem('pwa-prompt-shown') === 'true';

        if (hasSeenPrompt) {
            const declinedAt = localStorage.getItem('pwa-prompt-declined-at');
            if (declinedAt) {
                const daysSince = (Date.now() - parseInt(declinedAt)) / (1000 * 60 * 60 * 24);
                const declineCount = parseInt(
                    localStorage.getItem('pwa-prompt-decline-count') || '0',
                );

                if (declineCount >= 3) {
                    return false;
                }

                const daysToWait = declineCount === 0 ? 30 : 90;
                return daysSince > daysToWait;
            }
            return false;
        }

        return true;
    }

    markPromptAsShown() {
        localStorage.setItem('pwa-prompt-shown', 'true');
    }

    markPromptAsDeclined(dontShowAgain = false) {
        localStorage.setItem('pwa-prompt-shown', 'true');

        if (dontShowAgain) {
            const declineCount = parseInt(localStorage.getItem('pwa-prompt-decline-count') || '0');
            localStorage.setItem('pwa-prompt-decline-count', String(declineCount + 1));
        }

        localStorage.setItem('pwa-prompt-declined-at', String(Date.now()));
    }

    handleBeforeInstallPrompt(e) {
        e.preventDefault();
        this.deferredPrompt = e;
    }

    handleAppInstalled() {
        localStorage.setItem('pwa-installed', 'true');
        localStorage.setItem('pwa-prompt-shown', 'true');
        this.deferredPrompt = null;
    }

    async promptInstall() {
        if (!this.deferredPrompt) {
            throw new Error('Install prompt not available');
        }

        this.deferredPrompt.prompt();

        const { outcome } = await this.deferredPrompt.userChoice;

        this.deferredPrompt = null;

        if (outcome === 'accepted') {
            localStorage.setItem('pwa-installed', 'true');
            localStorage.setItem('pwa-prompt-shown', 'true');
        } else {
            this.markPromptAsDeclined();
        }

        return outcome;
    }

    canInstall() {
        return this.deferredPrompt !== null;
    }

    isInstallable() {
        if (this.isPWA()) {
            return false;
        }

        if (this.isIOS()) {
            return true;
        }

        if (this.isAndroid()) {
            return true;
        }

        return false;
    }

    getInstallMethod() {
        if (this.canInstall()) {
            return 'automatic';
        }

        if (this.isInstallable()) {
            return 'manual';
        }

        return 'unavailable';
    }

    reset() {
        localStorage.removeItem('pwa-prompt-shown');
        localStorage.removeItem('pwa-prompt-declined-at');
        localStorage.removeItem('pwa-prompt-decline-count');
        localStorage.removeItem('pwa-installed');
        this.deferredPrompt = null;
    }
}

export default new PWAService();
